import bem from "easy-bem";
import CardSample from "features/cases/blocks/CommonCard";
import { buildContentRoute } from "features/links/utils/shortLink";
import React from "react";

import { Card, Problem, Solution } from "../types/basic";
import { formatDate } from "../utils/basic";

import type { SortField } from "./SortToggle.types";

import "./UserContent.css";

const cn = bem("user-content");

type ContentItemsViewProps = {
  items: Array<Card | Problem | Solution>;
  solutions: Solution[];
  showList: boolean;
  sortField: SortField;
  openLinksInNewWindow?: boolean;
  isReviewing?: boolean;
  findVariantTitle?: (variantId: number | null) => string;
  onItemSaved?: (item: Card | Problem | Solution) => void;
  parentQueryId?: string;
  fromModeratorialFeed?: boolean;
  fromEditorialFeed?: boolean;
};

const ContentItemsView: React.FC<ContentItemsViewProps> = ({
  items,
  solutions,
  showList,
  sortField,
  openLinksInNewWindow = false,
  isReviewing = false,
  findVariantTitle,
  onItemSaved,
  parentQueryId,
  fromModeratorialFeed = false,
  fromEditorialFeed = false,
}) => {
  const dateHeader =
    sortField === "dateCreated" ? "Дата создания" : "Дата обновления";

  if (showList) {
    return (
      <div className={cn("list")}>
        <div
          className={cn("list-row")}
          key={`list-header`}
        >
          <div>{dateHeader}</div>
          <div>Тип публикации</div>
        </div>
        {items.map((item: Card | Problem | Solution) => {
          const isProblem = item.contentType === "problem";
          const isSolution = item.contentType === "solution";

          const date =
            sortField === "dateCreated"
              ? item.meta.dateCreated
              : item.meta.dateModified;

          const contentTypeLabel = isProblem
            ? "Проблема"
            : isSolution
              ? "Решение"
              : "Карточка";

          const getProblemLink = (problemId: number) => {
            return `/#/problems/${problemId}/`;
          };

          const getSolutionLink = (problemId: number, solutionId: number) => {
            return `/#/problems/${problemId}/solutions/${solutionId}/`;
          };

          const getCardLink = (card: Card) => {
            // problem обычно уже приходит с бэкенда даже для карточек,
            // привязанных к решению; solutions — запасной путь на случай,
            // если это не так.
            const problemId =
              card.problem ??
              (card.attributeOf === "solution"
                ? solutions.find((sol) => sol.id === card.solution)?.problem
                : undefined);

            if (!problemId) return "";

            const route = buildContentRoute({
              kind: "card",
              id: card.id,
              problemId,
              solutionId:
                card.attributeOf === "solution" ? card.solution : undefined,
            });

            return `/#${route.pathname}${route.hash}`;
          };

          const link = isProblem
            ? getProblemLink(item.id)
            : isSolution
              ? getSolutionLink((item as Solution).problem, item.id)
              : getCardLink(item as Card);

          return (
            <div
              className={cn("list-row")}
              key={`${item.contentType}-${item.id}`}
            >
              <div>{formatDate(date)}</div>

              <div>
                {contentTypeLabel}:&nbsp;&nbsp;
                <a
                  href={link}
                  target={openLinksInNewWindow ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                >
                  {item.title}
                </a>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn("cards")}>
      {items.map((item: Card | Problem | Solution) => {
        const isProblem = item.contentType === "problem";
        const isSolution = item.contentType === "solution";
        const isCard = item.contentType === "card";

        const attributeOf = isCard ? item.attributeOf : undefined;
        const cardProblemId = isCard ? (item as Card).problem : undefined;
        const cardRoute =
          isCard && cardProblemId
            ? buildContentRoute({
                kind: "card",
                id: item.id,
                problemId: cardProblemId,
                solutionId:
                  attributeOf === "solution"
                    ? (item as Card).solution
                    : undefined,
              })
            : undefined;

        return (
          <CardSample
            key={`${item.contentType}-${item.id}`}
            parentData={{
              isProblem,
              isSolution,
              isAttribute: isCard,
              problemId: String(
                isProblem
                  ? item.id
                  : isSolution
                    ? item.problem
                    : isCard
                      ? (item as Card).problem
                      : undefined,
              ),
              solutionId: String(
                isSolution
                  ? String(item.id)
                  : attributeOf === "solution"
                    ? (item as Card).solution
                    : undefined,
              ),
              attributeVariant: isCard
                ? findVariantTitle?.(item.attributeVariant)
                : undefined,
              //@ts-ignore
              sphereId: item.sphere || null,
              attributeOf,
              parentQueryId,
              fromModeratorialFeed,
              fromEditorialFeed,
            }}
            cardContentId={String(item.id)}
            sectionTitle={
              isProblem ? "Проблема" : isSolution ? "Решение" : undefined
            }
            objectColor={
              isProblem ? "#FFF2CB" : isSolution ? "#D4E8FF" : undefined
            }
            highlightColor={
              isProblem ? "#fcd757" : isSolution ? "#7bb8ff" : undefined
            }
            mode="wide"
            meta={item.meta}
            title={item.title}
            description={item.description || ""}
            url={
              isProblem
                ? `#/problems/${item.id}/`
                : isSolution
                  ? `#/problems/${item.problem}/solutions/${item.id}/`
                  : cardRoute
                    ? `#${cardRoute.pathname}${cardRoute.hash}`
                    : undefined
            }
            linkButtonText={
              isProblem
                ? "Подробнее о проблеме"
                : isSolution
                  ? "Подробнее о решении"
                  : cardRoute
                    ? "Перейти к карточке"
                    : undefined
            }
            openLinksInNewWindow={openLinksInNewWindow}
            editUrl="smth"
            isReviewing={isReviewing}
            onSaved={() => onItemSaved?.(item)}
          />
        );
      })}
    </div>
  );
};

export default ContentItemsView;
